import React, { useMemo, useState } from 'react';

// =============================================================================
//  Click-to-sort for the v3 tables
//
//  One hook plus one header cell, shared by every list. Each page supplies an
//  accessor per sortable column saying what to compare on; everything else —
//  direction toggling, the indicator, null handling, stable ordering — lives
//  here so the tables cannot drift apart the way their loading states did.
//
//  Sorting is client-side over the rows already in the store, which is what
//  the v3 lists render anyway. It composes with the existing search and chip
//  filters: filter first, then sort the result.
// =============================================================================

export type SortDir = 'asc' | 'desc';

export type SortValue = string | number | Date | null | undefined;

export interface SortState<K extends string> {
  key: K | null;
  dir: SortDir;
}

/** Columns whose natural first click should be "biggest/newest first". */
const DESC_FIRST_HINT = /date|value|amount|hours|size|fee|progress|since|year|count|when/i;

const normalise = (v: SortValue): string | number => {
  if (v === null || v === undefined) return '';
  if (v instanceof Date) return v.getTime();
  if (typeof v === 'number') return Number.isFinite(v) ? v : 0;
  return v;
};

export function useTableSort<T, K extends string>(
  rows: T[],
  accessors: Record<K, (row: T) => SortValue>,
  initial?: SortState<K>
) {
  const [sort, setSort] = useState<SortState<K>>(initial ?? { key: null, dir: 'asc' });

  const toggle = (key: K) => {
    setSort((prev) => {
      if (prev.key !== key) {
        // First click on a new column: dates and numbers are almost always
        // wanted newest/largest first.
        return { key, dir: DESC_FIRST_HINT.test(key) ? 'desc' : 'asc' };
      }
      return { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
    });
  };

  const sorted = useMemo(() => {
    if (!sort.key) return rows;
    const accessor = accessors[sort.key];
    if (!accessor) return rows;

    // decorate/sort/undecorate keeps the original index available, so equal
    // values retain their incoming order instead of shuffling between renders.
    return rows
      .map((row, index) => ({ row, index, value: normalise(accessor(row)) }))
      .sort((a, b) => {
        const { value: av } = a;
        const { value: bv } = b;

        // Blanks always sort last, whichever direction is active — an empty
        // cell is "unknown", not "smallest".
        const aBlank = av === '';
        const bBlank = bv === '';
        if (aBlank && bBlank) return a.index - b.index;
        if (aBlank) return 1;
        if (bBlank) return -1;

        let cmp: number;
        if (typeof av === 'number' && typeof bv === 'number') {
          cmp = av - bv;
        } else {
          cmp = String(av).localeCompare(String(bv), undefined, {
            numeric: true,
            sensitivity: 'base'
          });
        }
        if (cmp === 0) return a.index - b.index;
        return sort.dir === 'asc' ? cmp : -cmp;
      })
      .map((d) => d.row);
  }, [rows, sort, accessors]);

  return { sorted, sort, toggle };
}

interface ThProps<K extends string> {
  label: React.ReactNode;
  sortKey: K;
  sort: SortState<K>;
  onToggle: (key: K) => void;
  className?: string;
}

/** A `<th>` that sorts the table when clicked. */
export function SortableTh<K extends string>({
  label,
  sortKey,
  sort,
  onToggle,
  className
}: ThProps<K>) {
  const active = sort.key === sortKey;
  return (
    <th
      className={`sortable ${active ? 'sorted' : ''} ${className ?? ''}`.trim()}
      onClick={() => onToggle(sortKey)}
      // Header cells are not natively focusable, so make them behave like the
      // buttons they now are.
      role="columnheader"
      tabIndex={0}
      aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onToggle(sortKey);
        }
      }}
      title={`Sort by ${typeof label === 'string' ? label : sortKey}`}
    >
      <span className="th-inner">
        {label}
        <span className="sort-caret" aria-hidden="true">
          {active ? (sort.dir === 'asc' ? '▲' : '▼') : '↕'}
        </span>
      </span>
    </th>
  );
}
